/* eslint-disable camelcase */

import React, { useRef, useState, useEffect } from 'react';
import { withRouter } from 'react-router';
import {
  ConfigProvider, Row, Col, Form, DatePicker, Select, Radio, InputNumber, Input, message, Button,
} from 'antd';
import esLocale from 'antd/es/locale/es_ES';
import PropTypes from 'prop-types';
import {
  specialDates, Providers, ProvidersFlightHotel, ProvidersHotel,
} from 'utils/data/CreateBasicFlightData';
import generatePermalink from '../../utils/data/CreateFlightHotelPayload';
import './CreateBasicFlightForm.less';

const { Option } = Select;
const { Search } = Input;


/**
 * CreateBasicFlight:
 * This form is for the CreateBasicFlight data. This includes
 * departure date, provider, price and every data necessary to
 * create an Flight for an offer
 *
 * @author Francisco Suarez
 */
const CreateBasicFlight = (props) => {
  const {
    counterToEdit, destination, editableFlight, form, handleRequest, handleResetForm, iataId, isRound, token, isFlightHotel, location,
  } = props;

  const {
    getFieldDecorator, setFieldsValue, validateFields, resetFields, getFieldValue,
  } = form;

  if (!destination) {
    return null;
  }

  const locationPath = location.pathname.split('/');
  const isHotelPost = locationPath[1] === 'oferta-hotel';

  const datePickerRef = useRef(null);
  const [dataValidated, setDataValidated] = useState(false);
  const [bestdayForm, setBestdayForm] = useState(false);
  const [priceTravelForm, setPriceTravelForm] = useState(false);
  let providers = isFlightHotel ? ProvidersFlightHotel : Providers;
  if (isHotelPost) providers = ProvidersHotel;

  /**
   * Method that compare a given date with the current to identify
   * if it is a past date.
   * @param {date} current - current date
   * @returns Boolean
   */
  const disabledDate = (current) => current < new Date();

  /**
   * Method that compare a given date with the departure_date to identify
   * if it is a past date.
   * @param {date} current - current date
   * @returns Boolean
   */
  const disableReturnDate = (current) => current < form.getFieldValue('departure_date');

  /**
   * This method generates the provider permalink
   * @param {JSON} valuess
   */
  async function handleGeneratePermalink(values) {
    const result = await generatePermalink({
      ...values,
      destination,
      iataId,
      isRound,
      token,
    });
    setFieldsValue({ provider_permalink: result.provider_permalink });
    setDataValidated(true);
  }

  /**
   * Ask for a provider  permalink
   */
  const handleProviderPermalink = () => {
    validateFields(async (err, values) => {
      if (err) {
        if (Object.keys(err).length === 1 && Object.prototype.hasOwnProperty.call(err, 'provider_permalink')) {
          if (isFlightHotel) {
            if (['bestday', 'pricetravel'].includes(values.provider)) {
              await handleGeneratePermalink(values);
            } else {
              setDataValidated(true);
            }
            return;
          }
          if (values.provider === 'otra') {
            setDataValidated(true);
            return;
          }
          await handleGeneratePermalink(values);
        } else {
          message.warning('Se deben llenar los campos requeridos para general el link');
          setDataValidated(false);
        }
      } else {
        if (values.provider === 'otra' || isFlightHotel) {
          setDataValidated(true);
          return;
        }
        await handleGeneratePermalink(values);
      }
    });
  };

  /**
   * This method handles the form submission, validates it and then
   * sent to the parent container via handleRequest prop method
   * @param {*} e
   */
  function handleSubmit(e) {
    e.preventDefault();
    validateFields((err, values) => {
      if (!err) {
        datePickerRef.current.focus();
        handleRequest(
          {
            ...values,
            departure_iata: iataId,
            arrival_iata: destination,
          },
          Object.keys(editableFlight).length,
        );
        message.info(`Fecha agregada correctamente a ${iataId}`);
        setDataValidated(false);
        setFieldsValue({ provider_permalink: '' });
      }
    });
  }

  /**
   * Sets Arrival date to same value selected in Departure date
   * @param {Date} date Moment variable that
   */
  const setArrivalAsDeparture = (date) => {
    setFieldsValue({ arrival_date: date });
  };

  /**
   * Method to cancel the edit
   */
  const handleCancelOnClick = () => {
    handleResetForm();
    resetFields();
  };

  /**
   * Opens a generated permalink in a new tab when value exists
   */
  const checkPermalink = () => {
    const permalinkValue = getFieldValue('provider_permalink');
    if (permalinkValue) {
      window.open(permalinkValue, '_blank');
    }
  };

  const handleProviderChange = (provider) => {
    switch (provider) {
      case 'bestday':
        setBestdayForm(true);
        setPriceTravelForm(false);
        break;
      case 'pricetravel':
        setPriceTravelForm(true);
        setBestdayForm(false);
        break;
      default:
        setBestdayForm(false);
        setPriceTravelForm(false);
        break;
    }
  };

  /**
   * Use effect to render if comes a editableFlight
   */
  useEffect(() => {
    if (Object.keys(editableFlight).length) {
      handleProviderChange(editableFlight.flight.provider);
      setFieldsValue({
        ...editableFlight.flight,
      });
      setDataValidated(true);
    }
  }, [counterToEdit]);

  const adultsChildsFields = (
    <>
      <Col span={5}>
        <Form.Item label="Adultos">
          {getFieldDecorator('adults', {
            initialValue: 2,
          })(
            <Select>
              {Array.from(Array(10).keys()).map((c) => (
                <Option key={c} value={c}>
                  {c}
                </Option>
              ))}
            </Select>,
          )}
        </Form.Item>
        <Form.Item label="Infantes">
          {getFieldDecorator('childs', {
            initialValue: 0,
          })(
            <Select>
              {Array.from(Array(10).keys()).map((c) => (
                <Option key={c} value={c}>
                  {c}
                </Option>
              ))}
            </Select>,
          )}
        </Form.Item>
      </Col>
      <Col span={5}>
        <Form.Item label="Menores">
          {getFieldDecorator('youngs', {
            initialValue: 0,
          })(
            <Select>
              {Array.from(Array(10).keys()).map((c) => (
                <Option key={c} value={c}>
                  {c}
                </Option>
              ))}
            </Select>,
          )}
        </Form.Item>
      </Col>
    </>
  );

  const flightHotelExtraForm = (
    <Row
      gutter={[
        {
          xs: 8,
          sm: 16,
          md: 24,
          lg: 32,
        },
        20,
      ]}
      className="especial-hotel-form"
    >
      <Col span={(bestdayForm || priceTravelForm) ? 6 : 0}>
        <Form.Item label="Código hotel" hidden={!(bestdayForm || priceTravelForm)}>
          {getFieldDecorator('hotel_code', {
            rules: [
              {
                required: true,
                message: 'Por favor agregue un código de hotel',
              },
            ],
            initialValue: 'NA',
          })(<Input />)}
        </Form.Item>
        <Form.Item label="Nombre Hotel" hidden={!bestdayForm}>
          {getFieldDecorator('hotel_name', {
            rules: [
              {
                required: bestdayForm,
                message: 'Por favor agregue un Nombre de Hotel',
              },
            ],
            initialValue: 'NA',
          })(<Input />)}
        </Form.Item>
      </Col>
      <Col span={bestdayForm ? 6 : 0}>
        <Form.Item label="ID Origen" hidden={!bestdayForm}>
          {getFieldDecorator('origin_id', {
            rules: [
              {
                required: bestdayForm,
                message: 'Por favor agregue un ID Origen',
              },
            ],
            initialValue: 'NA',
          })(<Input />)}
        </Form.Item>
        <Form.Item label="ID Destino" hidden={!bestdayForm}>
          {getFieldDecorator('destination_id', {
            rules: [
              {
                required: bestdayForm,
                message: 'Por favor agregue un ID Destino',
              },
            ],
            initialValue: 'NA',
          })(<Input />)}
        </Form.Item>
      </Col>
      <Col span={priceTravelForm ? 6 : 0}>
        <Form.Item label="Mapping ID" hidden={!priceTravelForm}>
          {getFieldDecorator('mapping_id', {
            rules: [
              {
                required: priceTravelForm,
                message: 'Por favor agregue el valor para MappingID',
              },
            ],
            initialValue: 'NA',
          })(<Input />)}
        </Form.Item>
        <Form.Item label="Mapping Type" hidden={!priceTravelForm}>
          {getFieldDecorator('mapping_type', {
            rules: [
              {
                required: priceTravelForm,
                message: 'Por favor agregue el valor de Mapping Type',
              },
            ],
            initialValue: 'NA',
          })(<Input />)}
        </Form.Item>
      </Col>
      {adultsChildsFields}
      <Col span={24}>
        <Form.Item label="Comentario">
          {getFieldDecorator('comment', {
            rules: [
              {
                required: isFlightHotel,
                message: 'Por favor agregue un comentario',
              },
            ],
            initialValue: '',
          })(<Input />)}
        </Form.Item>
      </Col>
    </Row>
  );

  return (
    <Form>
      <ConfigProvider locale={esLocale}>
        <div>
          <Row
            gutter={[
              {
                xs: 8,
                sm: 16,
                md: 24,
                lg: 32,
              },
              0,
            ]}
          >
            <Col span={6}>
              <Form.Item label="Fecha de Salida">
                {getFieldDecorator('departure_date', {
                  rules: [
                    {
                      required: true,
                      message: 'Por favor agregue fecha de salida',
                    },
                  ],
                  initialValue: null,
                })(<DatePicker format="DD/MM/YYYY" disabledDate={disabledDate} ref={datePickerRef} onChange={setArrivalAsDeparture} />)}
              </Form.Item>
            </Col>

            {isRound ? (
              <Col span={6}>
                <Form.Item label="Fecha de Regreso">
                  {getFieldDecorator('arrival_date', {
                    rules: [
                      {
                        required: true,
                        message: 'Por favor agregue fecha de regreso',
                      },
                    ],
                  })(<DatePicker format="DD/MM/YYYY" disabledDate={disableReturnDate} disabled={form.getFieldValue('departure_date') === null} />)}
                </Form.Item>
              </Col>
            ) : null}

            <Col span={6}>
              <Form.Item label="Fecha Especial">
                {getFieldDecorator('special_date', {
                  initialValue: specialDates[0].value,
                })(
                  <Select>
                    {specialDates.map((d) => (
                      <Option key={d.value} value={d.value}>
                        {d.label}
                      </Option>
                    ))}
                  </Select>,
                )}
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item label="Fin de Semana">
                {getFieldDecorator('weekend', { initialValue: false })(
                  <Radio.Group>
                    <Radio value>Si</Radio>
                    <Radio value={false}>No</Radio>
                  </Radio.Group>,
                )}
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item label="Precio">
                {getFieldDecorator('price', {
                  rules: [{ required: true, message: 'Por favor agregue un precio' }],
                })(
                  <InputNumber
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                    max={999999}
                    min={1}
                    precision={2}
                  />,
                )}
              </Form.Item>
            </Col>

            {!isHotelPost && (
              <Col span={6}>
                <Form.Item label="Nº de Escalas">
                  {getFieldDecorator('connections', {
                    initialValue: 0,
                  })(
                    <Select>
                      {Array.from(Array(21).keys()).map((c) => (
                        <Option key={c} value={c}>
                          {c}
                        </Option>
                      ))}
                    </Select>,
                  )}
                </Form.Item>
              </Col>
            )}

            <Col span={6}>
              <Form.Item label="Moneda">
                {getFieldDecorator('currency', {
                  initialValue: 'mxn',
                })(
                  <Select disabled>
                    <Option key="mxn" value="mxn">
                      MXN
                    </Option>
                  </Select>,
                )}
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item label="Promo Code">{getFieldDecorator('promo_code')(<Input placeholder="NA" />)}</Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item label="Proveedor">
                {getFieldDecorator('provider', {
                  initialValue: providers[1].value,
                })(
                  <Select onChange={handleProviderChange}>
                    {providers.map((p) => (
                      <Option key={p.value} value={p.value}>
                        {p.label}
                      </Option>
                    ))}
                  </Select>,
                )}
              </Form.Item>
            </Col>
          </Row>
          {isFlightHotel && flightHotelExtraForm}
          {!isFlightHotel && isHotelPost && adultsChildsFields}
          <Row
            gutter={[
              {
                xs: 8,
                sm: 16,
                md: 24,
                lg: 32,
              },
              0,
            ]}
          >
            <Col span={18}>
              <Form.Item label="Permalink">
                {getFieldDecorator('provider_permalink', {
                  rules: [{ required: true, message: 'Por favor genere el Link' }],
                })(<Search enterButton={isFlightHotel ? 'Validar' : 'Generar'} onSearch={handleProviderPermalink} />)}
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Verificar Link">
                <Button className="go-to-permalink-button" icon="link" onClick={checkPermalink} />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Form.Item>
              {getFieldDecorator('expired', {
                initialValue: false,
              })(<Input hidden />)}
            </Form.Item>
            <div className="save-flight-row">
              <Form.Item>
                <Button type="primary" size="large" className="save-flight-button" disabled={!dataValidated} onClick={handleSubmit}>
                  {Object.keys(editableFlight).length > 0 ? 'Actualizar Fecha' : 'Guardar Fecha'}
                </Button>
              </Form.Item>
              {Object.keys(editableFlight).length > 0 && (
                <Button onClick={handleCancelOnClick} size="large" className="cancel-update-flight">
                  Cancelar
                </Button>
              )}
            </div>
          </Row>
        </div>
      </ConfigProvider>
    </Form>
  );
};

CreateBasicFlight.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  form: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  editableFlight: PropTypes.object.isRequired,
  counterToEdit: PropTypes.bool.isRequired,
  destination: PropTypes.string.isRequired,
  handleRequest: PropTypes.func.isRequired,
  handleResetForm: PropTypes.func.isRequired,
  iataId: PropTypes.string.isRequired,
  isRound: PropTypes.bool.isRequired,
  token: PropTypes.string.isRequired,
  isFlightHotel: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  location: PropTypes.object.isRequired,
};

const CreateBasicFlightForm = Form.create({ name: 'simpleFlightForm' })(CreateBasicFlight);

export default withRouter(CreateBasicFlightForm);
